import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  LockClosedIcon,
  XMarkIcon,
  UserIcon,
  SparklesIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { chatAPI } from '../services/api';
import { useLicense } from '../hooks/useLicense';
import { useAuthStore } from '../store/authStore';

// Conversations — surfaces ALL chat conversations for the tenant (lead or not,
// including anonymous widget visitors). Grid of first-message / agent / when /
// count / outcome, with a modal to read the full transcript. Read-only (P1).

const PAGE_SIZE = 25;

const CHANNELS = {
  web: ['Website', 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'],
  whatsapp: ['WhatsApp', 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'],
  instagram: ['Instagram DM', 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-200'],
  public_link: ['Agent link', 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200'],
  qr: ['QR / offline', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
};
const CAMPAIGN_SOURCES = {
  qr: 'QR / offline marketing', qr_code: 'QR / offline marketing',
  offline: 'Offline marketing', flyer: 'Flyer', brochure: 'Brochure',
  instagram: 'Instagram', insta: 'Instagram', ig: 'Instagram',
  whatsapp: 'WhatsApp', wa: 'WhatsApp', wati: 'WATI', gallabox: 'Gallabox',
  google: 'Google', google_ads: 'Google Ads', facebook: 'Facebook', fb: 'Facebook',
  email: 'Email', sms: 'SMS',
};

const ConversationChannel = ({ conversation }) => {
  const [label, style] = CHANNELS[conversation.channel] || [
    'Unknown', 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  ];
  const source = conversation.campaign_source;
  const sourceLabel = source && (CAMPAIGN_SOURCES[source.toLowerCase()] || source);
  return (
    <div className="min-w-0 max-w-48 space-y-1">
      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${style}`}>{label}</span>
      {sourceLabel && <div className="text-xs text-gray-500 dark:text-gray-400 break-words">Via {sourceLabel}</div>}
      {conversation.campaign_name && <div className="text-xs text-gray-500 dark:text-gray-400 break-words">{conversation.campaign_name}</div>}
    </div>
  );
};

// Synthetic preview data for Starter admins. It must never be mixed with a
// tenant's real conversations: the entitlement branch below returns this
// preview before any conversation API request can run.
const SAMPLE_CONVERSATIONS = [
  {
    id: 'sample-google-ad',
    title: 'Pricing and callback request',
    first_message: 'I saw your Google ad. Which plan is right for a growing team?',
    created_at: '2026-08-30T05:30:00.000Z',
    message_count: 6,
    outcome: 'qualified',
    is_anonymous: true,
    lead_id: 'sample-lead',
    source: 'Google ad',
    messages: [
      { role: 'user', created_at: '2026-08-30T05:30:00.000Z', content: 'I saw your Google ad. Which plan is right for a growing team?' },
      { role: 'assistant', created_at: '2026-08-30T05:30:08.000Z', content: 'Growth is designed for teams that need more AI answers, agents and real-time lead alerts. What are you hoping to automate?' },
      { role: 'user', created_at: '2026-08-30T05:31:02.000Z', content: 'Website enquiries and WhatsApp follow-up. We receive around 40 enquiries a day.' },
      { role: 'assistant', created_at: '2026-08-30T05:31:10.000Z', content: 'That sounds like a good fit for Growth. Would you like your sales team to call you and walk through the setup?' },
      { role: 'user', created_at: '2026-08-30T05:31:38.000Z', content: 'Yes, tomorrow at 11 AM works. I have shared my number in the contact form.' },
      { role: 'assistant', created_at: '2026-08-30T05:31:44.000Z', content: 'Done — I have captured your callback request for tomorrow at 11 AM.' },
    ],
  },
  {
    id: 'sample-qr',
    title: 'QR campaign enquiry',
    first_message: 'I scanned the flyer. Can I book a site visit this Saturday?',
    created_at: '2026-08-29T10:15:00.000Z',
    message_count: 4,
    outcome: 'lead',
    is_anonymous: true,
    lead_id: 'sample-lead-2',
    source: 'QR code',
    messages: [
      { role: 'user', created_at: '2026-08-29T10:15:00.000Z', content: 'I scanned the flyer. Can I book a site visit this Saturday?' },
      { role: 'assistant', created_at: '2026-08-29T10:15:07.000Z', content: 'Yes. Would you prefer morning or afternoon, and which project are you interested in?' },
      { role: 'user', created_at: '2026-08-29T10:15:31.000Z', content: 'Morning, for the Whitefield project.' },
      { role: 'assistant', created_at: '2026-08-29T10:15:39.000Z', content: 'Thanks. I have captured a Saturday morning site-visit request for Whitefield so the team can confirm the slot.' },
    ],
  },
  {
    id: 'sample-website',
    title: 'Service availability question',
    first_message: 'Do you provide same-day service in Indiranagar?',
    created_at: '2026-08-28T13:45:00.000Z',
    message_count: 3,
    outcome: 'engaged',
    is_anonymous: true,
    lead_id: null,
    source: 'Website',
    messages: [
      { role: 'user', created_at: '2026-08-28T13:45:00.000Z', content: 'Do you provide same-day service in Indiranagar?' },
      { role: 'assistant', created_at: '2026-08-28T13:45:06.000Z', content: 'Same-day availability depends on the service and the time of your request. Which service do you need?' },
      { role: 'user', created_at: '2026-08-28T13:45:29.000Z', content: 'Just checking for now, thank you.' },
    ],
  },
];

const OUTCOME_STYLES = {
  lead: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  qualified: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  engaged: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  abandoned: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  unanswered: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  deflected: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
};

function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const OutcomeBadge = ({ outcome }) => {
  if (!outcome) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">—</span>;
  }
  const cls = OUTCOME_STYLES[String(outcome).toLowerCase()] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${cls}`}>
      {outcome}
    </span>
  );
};

const SampleTranscriptModal = ({ conversation, onClose }) => {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Sample conversation transcript">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">{conversation.title}</h3>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Sample</span>
            </div>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {conversation.source} &middot; Synthetic preview &middot; No customer data
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Close sample conversation">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50 dark:bg-gray-900/40">
          {conversation.messages.map((message, index) => {
            const isUser = message.role === 'user';
            return (
              <div key={`${conversation.id}-${index}`} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                  isUser
                    ? 'bg-indigo-600 text-white rounded-br-sm'
                    : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-sm'
                }`}>
                  <div className="whitespace-pre-wrap break-words">{message.content}</div>
                  <div className={`mt-1 text-[10px] ${isUser ? 'text-right text-indigo-200' : 'text-gray-400 dark:text-gray-500'}`}>
                    {fmtWhen(message.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">CSV and JSON export unlock with Growth.</p>
          <div className="flex items-center gap-2">
            <button disabled title="Available on Growth and Scale" className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-400 opacity-70 dark:border-gray-700 dark:text-gray-500">
              <LockClosedIcon className="w-3.5 h-3.5" /> CSV
            </button>
            <button disabled title="Available on Growth and Scale" className="inline-flex cursor-not-allowed items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-400 opacity-70 dark:border-gray-700 dark:text-gray-500">
              <LockClosedIcon className="w-3.5 h-3.5" /> JSON
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ConversationHistoryUpgrade = ({ tier, billingPath }) => {
  const [sampleOpen, setSampleOpen] = useState(null);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <section className="rounded-xl bg-gradient-to-r from-indigo-600 to-blue-600 p-5 text-white shadow-md sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-5">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold ring-1 ring-white/25">
                <LockClosedIcon className="w-3.5 h-3.5" /> Growth+
              </span>
              <span className="text-sm font-medium text-indigo-100">Admin access</span>
            </div>
            <h1 className="mt-4 text-2xl font-bold">Conversation history &amp; export</h1>
            <p className="mt-2 text-sm leading-6 text-indigo-50">
              Browse every visitor conversation, read the full transcript, and download it as CSV or JSON.
            </p>
            <p className="mt-3 text-sm leading-6 text-indigo-100">
              This feature is available to workspace admins on the Growth and Scale plans. Your current plan is{' '}
              <span className="font-semibold text-white capitalize">{tier || 'Starter'}</span>.
            </p>
          </div>
          <Link
            to={billingPath}
            className="inline-flex items-center justify-center rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600"
          >
            View plans
          </Link>
        </div>
      </section>

      <section className="mt-6" aria-labelledby="sample-conversations-title">
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 id="sample-conversations-title" className="text-xl font-bold text-gray-900 dark:text-white">See how conversation history works</h2>
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Sample data</span>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Open a synthetic example below. Nothing shown here comes from your visitors.
            </p>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3 font-medium">First message</th>
                  <th className="px-4 py-3 font-medium">Source</th>
                  <th className="px-4 py-3 text-center font-medium">Msgs</th>
                  <th className="px-4 py-3 font-medium">Outcome</th>
                  <th className="px-4 py-3 text-right font-medium">&nbsp;</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {SAMPLE_CONVERSATIONS.map((conversation) => (
                  <tr key={conversation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                    <td className="px-4 py-3">
                      <button type="button" onClick={() => setSampleOpen(conversation)} className="max-w-md text-left group">
                        <span className="line-clamp-1 font-medium text-gray-900 group-hover:text-indigo-600 dark:text-gray-100 dark:group-hover:text-indigo-400">
                          {conversation.first_message}
                        </span>
                        <span className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                          <UserIcon className="h-3 w-3" /> Sample visitor
                          {conversation.lead_id && <span className="ml-1 text-green-600 dark:text-green-400">&middot; Lead captured</span>}
                        </span>
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-500 dark:text-gray-400">{conversation.source}</td>
                    <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">{conversation.message_count}</td>
                    <td className="px-4 py-3"><OutcomeBadge outcome={conversation.outcome} /></td>
                    <td className="px-4 py-3 text-right">
                      <button type="button" onClick={() => setSampleOpen(conversation)} className="text-sm font-medium text-indigo-600 hover:underline dark:text-indigo-400">
                        View sample
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {sampleOpen && <SampleTranscriptModal conversation={sampleOpen} onClose={() => setSampleOpen(null)} />}
    </div>
  );
};

function download(filename, content, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvEscape(v) {
  let s = v == null ? '' : String(v);
  // Formula-injection guard: transcript content is visitor-controlled, so a
  // message starting with = + - @ (or tab/CR) could be run as a formula when the
  // CSV is opened in Excel/Sheets. Prefix a single quote to neutralize it.
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function transcriptToCsv(data) {
  const rows = [['role', 'timestamp', 'content']];
  (data.messages || []).forEach((m) => rows.push([m.role, m.created_at || '', m.content || '']));
  return rows.map((r) => r.map(csvEscape).join(',')).join('\r\n');
}

const TranscriptModal = ({ conversationId, tenantId, onClose, onDeleted }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    chatAPI.getConversationTranscript(conversationId, tenantId ? { tenant_id: tenantId } : {})
      .then((res) => { if (alive) setData(res); })
      .catch((e) => { if (alive) setError(e?.response?.data?.detail || 'Could not load transcript'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [conversationId, tenantId]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const baseName = `conversation-${String(conversationId).slice(0, 8)}`;
  const doDownloadJson = () => download(`${baseName}.json`, JSON.stringify(data, null, 2), 'application/json');
  const doDownloadCsv = () => download(`${baseName}.csv`, transcriptToCsv(data), 'text/csv;charset=utf-8');

  const doDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await chatAPI.deleteConversationRecord(conversationId, tenantId ? { tenant_id: tenantId } : {});
      if (onDeleted) onDeleted();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to delete conversation');
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] flex flex-col bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white truncate">
              {loading ? 'Loading…' : (data?.title || 'Conversation')}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {data && (
                <>
                  {fmtWhen(data.created_at)}
                  {data.is_anonymous ? ' · Anonymous visitor' : ''}
                  {data.agent_id ? ` · Agent ${String(data.agent_id).slice(0, 8)}` : ''}
                </>
              )}
            </p>
            {data && <div className="mt-2"><ConversationChannel conversation={data} /></div>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-gray-50 dark:bg-gray-900/40">
          {loading && <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">Loading transcript…</p>}
          {error && <p className="text-sm text-red-600 dark:text-red-400 text-center py-8">{error}</p>}
          {!loading && !error && (data?.messages || []).length === 0 && (
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">No messages in this conversation.</p>
          )}
          {!loading && !error && (data?.messages || []).map((m, i) => {
            const isUser = m.role === 'user';
            return (
              <div key={i} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                  isUser
                    ? 'bg-indigo-600 text-white rounded-br-sm'
                    : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-700 rounded-bl-sm'
                }`}>
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                  <div className={`text-[10px] mt-1 ${isUser ? 'text-indigo-200 text-right' : 'text-gray-400 dark:text-gray-500'}`}>
                    {fmtWhen(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {!loading && !error && data && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700">
            {confirmingDelete ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-red-600 dark:text-red-400">
                  Permanently delete this conversation and its messages? This can&rsquo;t be undone.
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => setConfirmingDelete(false)} disabled={deleting}
                    className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                    Cancel
                  </button>
                  <button onClick={doDelete} disabled={deleting}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button onClick={doDownloadCsv}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <ArrowDownTrayIcon className="w-4 h-4" /> CSV
                  </button>
                  <button onClick={doDownloadJson}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                    <ArrowDownTrayIcon className="w-4 h-4" /> JSON
                  </button>
                </div>
                <button onClick={() => setConfirmingDelete(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30">
                  <TrashIcon className="w-4 h-4" /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ConversationsPage = () => {
  // tenantId from /t/:tenantId is passed through so a superadmin impersonating a
  // tenant is scoped to it; the backend ignores it for regular users (always own
  // tenant). Absent on the platform view, where superadmin sees all tenants.
  const { tenantId } = useParams();
  const { user } = useAuthStore();
  const { license, tier, isGrowthOrScale, isLoading: licenseLoading } = useLicense();
  const isSuperadmin = user?.role === 'superadmin' || user?.is_superuser;
  const featureEntitlements = license?.feature_entitlements;
  const hasConversationHistoryEntitlement = Object.prototype.hasOwnProperty.call(
    featureEntitlements || {},
    'conversation_history'
  );
  // The API response is the authority because a superadmin can make a
  // per-tenant exception. The tier fallback prevents a brief mixed-version
  // deploy from locking a paid tenant while an older API is still responding.
  const conversationHistoryEnabled = isSuperadmin || (
    hasConversationHistoryEntitlement
      ? featureEntitlements.conversation_history === true
      : isGrowthOrScale
  );
  const billingPath = tenantId ? `/t/${tenantId}/billing` : '/billing';
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ?open=<conversation id> opens that transcript, e.g. from Missed opportunities.
  const [searchParams] = useSearchParams();
  const [openId, setOpenId] = useState(() => searchParams.get('open'));
  // Age filter + sort both key off created_at ("Started"), never updated_at:
  // updated_at carries onupdate, so any write to a row would make an old
  // conversation look recent and hide it from a cleanup filter.
  const [olderThanDays, setOlderThanDays] = useState('');
  const [sort, setSort] = useState('started_desc');
  // Our own smoke canaries write ~16 conversations per environment per day into
  // reserved tenants and nothing deletes them. The server hides those by
  // default; this only ever asks for them back. Platform view only -- when a
  // tenant is in scope the server does not filter, so a control here would be a
  // checkbox that does nothing.
  const [includeSystemTraffic, setIncludeSystemTraffic] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = useCallback(() => {
    if (!conversationHistoryEnabled) return;
    setLoading(true);
    setError(null);
    chatAPI.listAllConversations({
      skip,
      limit: PAGE_SIZE,
      sort,
      ...(olderThanDays ? { older_than_days: Number(olderThanDays) } : {}),
      ...(tenantId ? { tenant_id: tenantId } : {}),
      ...(!tenantId && includeSystemTraffic ? { include_system_traffic: true } : {}),
    })
      .then((res) => {
        setRows(res.conversations || []);
        setTotal(res.total || 0);
        // Selection is per result set. Keeping ids across a filter change would
        // let you delete rows you can no longer see.
        setSelected(new Set());
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Could not load conversations'))
      .finally(() => setLoading(false));
  }, [skip, tenantId, sort, olderThanDays, includeSystemTraffic, conversationHistoryEnabled]);

  useEffect(() => {
    if (!licenseLoading) load();
  }, [load, licenseLoading]);

  const allOnPageSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAllOnPage = () => {
    setSelected(allOnPageSelected ? new Set() : new Set(rows.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const runBulkDelete = async () => {
    if (selected.size === 0 || bulkDeleting) return;
    setBulkDeleting(true);
    try {
      const res = await chatAPI.bulkDeleteConversations(
        Array.from(selected),
        tenantId ? { tenant_id: tenantId } : {}
      );
      // requested vs deleted differ when a row was already gone or belonged to
      // another tenant. Say so rather than claim they all went.
      if (res.deleted < res.requested) {
        toast.success(`Deleted ${res.deleted} of ${res.requested} — the rest were already gone or unavailable`);
      } else {
        toast.success(`Deleted ${res.deleted} conversation${res.deleted === 1 ? '' : 's'}`);
      }
      setConfirmBulk(false);
      setSelected(new Set());
      // setSkip(0) re-runs the load effect on its own. Calling load() as well
      // fired twice when skip was non-zero: once for the old page, once for
      // page zero.
      if (skip === 0) load(); else setSkip(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete the selected conversations');
    } finally {
      setBulkDeleting(false);
    }
  };

  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + PAGE_SIZE, total);

  if (licenseLoading && !isSuperadmin) {
    return (
      <div className="flex items-center justify-center h-96 text-sm text-gray-500 dark:text-gray-400">
        Loading plan access…
      </div>
    );
  }

  if (!conversationHistoryEnabled) {
    return <ConversationHistoryUpgrade tier={tier} billingPath={billingPath} />;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ChatBubbleLeftRightIcon className="w-6 h-6 text-indigo-500" />
            Conversations
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Every chat your agents have had &mdash; lead or not, including anonymous visitors. Open one to read the full transcript.
          </p>
        </div>
        <div className="mt-1 flex items-center gap-2">
          <select
            value={olderThanDays}
            onChange={(e) => { setOlderThanDays(e.target.value); setSkip(0); }}
            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            title="Filter by when the conversation started"
          >
            <option value="">All conversations</option>
            <option value="30">Started over 30 days ago</option>
            <option value="90">Started over 90 days ago</option>
            <option value="180">Started over 180 days ago</option>
          </select>
          {!tenantId && (
            <label
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer select-none"
              title="Show conversations generated by the daily smoke canaries. Hidden by default because they outnumber real ones."
            >
              <input
                type="checkbox"
                checked={includeSystemTraffic}
                onChange={(e) => { setIncludeSystemTraffic(e.target.checked); setSkip(0); }}
                className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
              />
              Include system test traffic
            </label>
          )}
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <ArrowPathIcon className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2.5">
          <span className="text-sm text-indigo-900 dark:text-indigo-200">
            {selected.size} selected on this page
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 hover:underline"
            >
              Clear
            </button>
            <button
              onClick={() => setConfirmBulk(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              <TrashIcon className="w-4 h-4" />
              Delete selected
            </button>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400">
              <tr className="text-left">
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allOnPageSelected}
                    onChange={toggleAllOnPage}
                    disabled={rows.length === 0}
                    aria-label="Select all on this page"
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                </th>
                <th className="px-4 py-3 font-medium">First message</th>
                <th className="px-4 py-3 font-medium">Channel / source</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => { setSort(sort === 'started_desc' ? 'started_asc' : 'started_desc'); setSkip(0); }}
                    className="inline-flex items-center gap-1 font-medium hover:text-gray-900 dark:hover:text-white"
                    title="Sort by when the conversation started"
                  >
                    Started
                    <span aria-hidden="true">{sort === 'started_asc' ? '↑' : '↓'}</span>
                  </button>
                </th>
                <th className="px-4 py-3 font-medium text-center">Msgs</th>
                <th className="px-4 py-3 font-medium">Outcome</th>
                <th className="px-4 py-3 font-medium text-right">&nbsp;</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500 dark:text-gray-400">Loading conversations…</td></tr>
              )}
              {!loading && error && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-red-600 dark:text-red-400">{error}</td></tr>
              )}
              {!loading && !error && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">No conversations yet.</td></tr>
              )}
              {!loading && !error && rows.map((r) => (
                <tr key={r.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/40 ${selected.has(r.id) ? 'bg-indigo-50/60 dark:bg-indigo-900/20' : ''}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleOne(r.id)}
                      aria-label="Select conversation"
                      className="rounded border-gray-300 dark:border-gray-600"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setOpenId(r.id)} className="text-left group max-w-md">
                      <span className="text-gray-900 dark:text-gray-100 font-medium group-hover:text-indigo-600 dark:group-hover:text-indigo-400 line-clamp-1">
                        {r.first_message || '(no message)'}
                      </span>
                      <span className="flex items-center gap-1.5 mt-0.5 text-xs text-gray-400 dark:text-gray-500">
                        {r.is_anonymous ? <UserIcon className="w-3 h-3" /> : <SparklesIcon className="w-3 h-3" />}
                        {r.is_anonymous ? 'Anonymous' : 'Signed-in'}
                        {r.lead_id && <span className="ml-1 text-green-600 dark:text-green-400">· Lead</span>}
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-3"><ConversationChannel conversation={r} /></td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500 dark:text-gray-400">{fmtWhen(r.created_at)}</td>
                  <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">{r.message_count}</td>
                  <td className="px-4 py-3"><OutcomeBadge outcome={r.outcome} /></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setOpenId(r.id)} className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline">
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && !error && total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
            <span>{from}&ndash;{to} of {total}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                disabled={skip === 0}
                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Prev
              </button>
              <button
                onClick={() => setSkip(skip + PAGE_SIZE)}
                disabled={to >= total}
                className="px-3 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
        Outcome is derived by the nightly AI insight job and may show &ldquo;&mdash;&rdquo; until a conversation has been classified.
      </p>

      {confirmBulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
              <TrashIcon className="w-5 h-5 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete {selected.size} conversation{selected.size === 1 ? '' : 's'}?
              </h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Removes each conversation, every message in it, and its AI insight
                row. Your agents lose the ability to show these transcripts.
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Captured leads are <span className="font-semibold text-gray-900 dark:text-white">not</span>{' '}
                deleted &mdash; a lead keeps its own copy of the chat and is removed
                separately from Leads.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                This cannot be undone.
              </p>
            </div>
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                onClick={() => setConfirmBulk(false)}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={runBulkDelete}
                disabled={bulkDeleting}
                className="px-4 py-2 text-sm rounded-md bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkDeleting ? 'Deleting…' : `Delete ${selected.size}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {openId && (
        <TranscriptModal
          conversationId={openId}
          tenantId={tenantId}
          onClose={() => setOpenId(null)}
          onDeleted={load}
        />
      )}
    </div>
  );
};

export default ConversationsPage;
